import { TerciarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import AddHotelGroupModal from 'components/ReusableComponents/ManageHotel/AddHotelGroup'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import SearchTextInput from 'components/ReusableComponents/SearchTextInput'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import { hotelStatusNames, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import contractApi from 'services/api/contracts'
import subsidiariesApi from 'services/api/subsidiaries'

import styles from './GroupContractList.module.scss'

const activeColor = '#428BE1'
const goodColor = '#66BB6A'
const badColor = '#CAB6B6'

const statusToColor = {
  New: badColor,
  Pending: activeColor,
  Finalized: goodColor
}

const allStatuses = {
  New: 'Not sent',
  Pending: 'Pending',
  Finalized: 'Finalized',
  ...hotelStatusNames
}

const managementStatusOptions = {
  Any: 'Any status',
  Pending: 'Pending',
  New: 'Not sent',
  Finalized: 'Finalized'
}

const independentStatusOptions = {
  Any: 'Any status',
  ...hotelStatusNamesFilterable
}

const ContractStatusPill = ({ status }) => {
  return (
    <span
      style={{
        backgroundColor: statusToColor[status],
        color: 'white',
        fontSize: 12,
        lineHeight: '13px',
        fontWeight: 500,
        padding: '4px 7px',
        width: 70,
        textAlign: 'center',
        borderRadius: 20,
        textTransform: 'uppercase'
      }}
    >
      {allStatuses[status]}
    </span>
  )
}

const GroupContractList = () => {
  const history = useHistory()

  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [tab, setTab] = useState('management')
  const [managementGroups, setManagementGroups] = useState([])
  const [independentHotels, setEndependentHotels] = useState([])

  const [statusToFilter, setStatusToFilter] = useState('Any')

  const [isAddGroupDialogVisible, setIsAddGroupDialogVisible] = useState(false)

  const [groupIndex, setGroupIndex] = useState(0)

  const [hotelsByMerchantId, setHotelsByMerchantId] = useState({})

  useEffect(() => {
    subsidiariesApi.getHotelList({ includingDeleted: false }).then(res => {
      const hotels = res.reduce((acc, curr) => {
        const existingHotels = acc[curr.id] || []

        return { ...acc, [curr.id]: [...existingHotels, curr.subsidiary] }
      }, {})

      setHotelsByMerchantId(hotels)
    })
  }, [])

  useEffect(() => {
    contractApi.internalAdmin.list().then(res => {
      setManagementGroups(res.merchants)
      setEndependentHotels(res.independentlyManagedHotels)
      setIsLoading(false)
    })
  }, [groupIndex])

  if (isLoading) {
    return (
      <>
        <CustomHeader title="Contracts" width={1080} />
        <LoadingIndicator />
      </>
    )
  }

  function getHotelCount(contract) {
    try {
      return JSON.parse(contract.data).hotels.length
    } catch (err) {
      return ''
    }
  }

  function getActiveHotelCount(contract) {
    return hotelsByMerchantId[contract.merchantId]?.length
  }

  function handleNewGroupAdded() {
    setGroupIndex(groupIndex + 1)
    setIsAddGroupDialogVisible(false)
  }

  return (
    <div className={styles.wrapper}>
      {isAddGroupDialogVisible && (
        <AddHotelGroupModal
          closeModal={() => setIsAddGroupDialogVisible(false)}
          switchToHotel={() => handleNewGroupAdded()}
        />
      )}
      <CustomHeader title="Contracts" width={1080} isFixed>
        <button
          className={styles.addGroupButton}
          onClick={() => {
            if (tab === 'management') {
              setIsAddGroupDialogVisible(true)
            } else {
              history.push('/contracts/new/s')
            }
          }}
        >
          <AddIcon />{' '}
          {tab === 'management'
            ? 'Add new Management group'
            : 'Independently managed hotel'}
        </button>
      </CustomHeader>
      <div className={styles.tabBar}>
        <button
          className={tab === 'management' ? styles.activeTab : styles.tab}
          onClick={() => setTab('management')}
        >
          Management groups
        </button>
        <button
          className={tab === 'independent' ? styles.activeTab : styles.tab}
          onClick={() => setTab('independent')}
        >
          Independently Managed Hotels
        </button>
      </div>
      <div className={styles.filterBar}>
        <SearchTextInput
          placeholder="Search with group name"
          handleChange={setSearchQuery}
        />
        <Dropdown
          value={statusToFilter}
          onChange={e => setStatusToFilter(e.target.value)}
          options={
            tab === 'management' ? managementStatusOptions : independentStatusOptions
          }
        />
      </div>
      {tab === 'management' ? (
        <Table widths={[300, 180, 200, 70, 70, 40, 115]}>
          <TitleRow>
            <TitleCell>Group</TitleCell>
            <TitleCell>Contact</TitleCell>
            <TitleCell>Last updated on</TitleCell>
            <TitleCell>Pending</TitleCell>
            <TitleCell>Active</TitleCell>
            <TitleCell>Status</TitleCell>
            <TitleCell></TitleCell>
          </TitleRow>
          {managementGroups
            .filter(contract =>
              contract.merchantName.toUpperCase().includes(searchQuery.toUpperCase())
            )
            .filter(contract =>
              statusToFilter === 'Any' ? true : contract.status === statusToFilter
            )
            .sort((a, b) => a.merchantName.localeCompare(b.merchantName))
            .map(contract => (
              <Row key={contract.merchantId} className={styles.row}>
                <Cell className={styles.groupNameCell}>{contract.merchantName}</Cell>
                <Cell>{contract.fullName}</Cell>
                <Cell>
                  {moment(contract.lastUpdatedOn).format('MMM DD, YYYY, hh:MM A')}
                </Cell>
                <Cell>{getHotelCount(contract)}</Cell>
                <Cell>{getActiveHotelCount(contract)}</Cell>
                <Cell>
                  <ContractStatusPill status={contract.status} />
                </Cell>
                <Cell className={styles.ctaCell}>
                  <Link to={`/contracts/edit/m/${contract.merchantId}`}>
                    <TerciarySmallButton>Edit</TerciarySmallButton>
                  </Link>
                </Cell>
              </Row>
            ))}
        </Table>
      ) : (
        <Table widths={[500, 400]}>
          <TitleRow>
            <TitleCell>Name</TitleCell>
            <TitleCell>Status</TitleCell>
            <TitleCell></TitleCell>
          </TitleRow>
          {independentHotels
            .filter(hotel =>
              hotel.hotelName.toUpperCase().includes(searchQuery.toUpperCase())
            )
            .filter(contract =>
              statusToFilter === 'Any' ? true : contract.status === statusToFilter
            )
            .sort((a, b) => a.hotelName.localeCompare(b.hotelName))
            .map(hotel => (
              <Row key={hotel.id} className={styles.row}>
                <Cell className={styles.groupNameCell}>{hotel.hotelName}</Cell>
                <Cell>{hotel.status}</Cell>
                <Cell className={styles.ctaCell}>
                  <Link to={`/contracts/edit/s/${hotel.id}`}>
                    <TerciarySmallButton>Edit</TerciarySmallButton>
                  </Link>
                </Cell>
              </Row>
            ))}
        </Table>
      )}
    </div>
  )
}

export default GroupContractList
